import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useBadgeQuery } from './BadgeResourceContext';
import { useAuth } from './AuthContext';
import { useArtistAuth } from './ArtistAuthContext';
import { resolveAdminPermission } from '../constants/adminPermissionCatalog';
import {
  dismissAnnouncements,
  getAdminAnnouncementPendingCount,
  listAnnouncementFeed,
} from '../api/announcements';

const AnnouncementContext = createContext(null);
const POPUP_DISMISS_PREFIX = 'casticart:announcement-popup-dismissals:';
const MAX_LOCAL_DISMISSALS = 100;
const LATEST_FEED_LIMIT = 5;
const POPUP_FEED_LIMIT = 100;

const buildDismissalStorageKey = (viewerScope) =>
  viewerScope ? `${POPUP_DISMISS_PREFIX}${encodeURIComponent(viewerScope)}` : null;

const readDismissalTokens = (viewerScope) => {
  const key = buildDismissalStorageKey(viewerScope);
  if (!key || typeof window === 'undefined') return [];
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || '[]');
    return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : [];
  } catch {
    return [];
  }
};

const writeDismissalTokens = (viewerScope, tokens) => {
  const key = buildDismissalStorageKey(viewerScope);
  if (!key || typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(key, JSON.stringify(tokens.slice(-MAX_LOCAL_DISMISSALS)));
  } catch {
    // Server acknowledgement remains authoritative when storage is unavailable.
  }
};

const buildPopupToken = (announcement) => {
  if (!announcement) return null;
  const timestamp =
    announcement.publishedAt ||
    announcement.primaryPublishedAt ||
    announcement.publishAt ||
    announcement.createdAt;
  if (!timestamp) return null;
  return `${announcement.id}:${new Date(timestamp).toISOString()}`;
};

const popupPublishedAt = (announcement) => {
  const timestamp =
    announcement?.publishedAt ||
    announcement?.primaryPublishedAt ||
    announcement?.publishAt ||
    announcement?.createdAt;
  const parsed = timestamp ? new Date(timestamp).getTime() : 0;
  return Number.isFinite(parsed) ? parsed : 0;
};

const selectLatestPopupAnnouncement = (announcements) =>
  [...(announcements || [])]
    .filter(
      (item) =>
        item?.popupEnabled &&
        item?.status === 'published' &&
        item?.popupActive !== false &&
        item?.popupExpired !== true &&
        item?.popupEligible !== false
    )
    .sort((left, right) => {
      const timestampDifference = popupPublishedAt(right) - popupPublishedAt(left);
      if (timestampDifference !== 0) return timestampDifference;
      return Number(right?.id || 0) - Number(left?.id || 0);
    })[0] || null;

export function AnnouncementProvider({ children }) {
  const { user } = useAuth();
  const { artistUser } = useArtistAuth();
  const isTaskOnlyAdmin =
    user?.isLoggedIn &&
    user?.userType === 'admin' &&
    String(user?.role || '').toLowerCase() === 'task_only';

  const activeRole = isTaskOnlyAdmin
    ? null
    : user?.isLoggedIn
    ? 'admin'
    : artistUser?.isLoggedIn
      ? 'artist'
      : null;
  const canViewAdminAnnouncements = useMemo(() => {
    if (!user?.isLoggedIn || activeRole !== 'admin') return false;
    return resolveAdminPermission({
      permissionKey: 'NAV_ANNOUNCEMENTS',
      role: user?.role,
      permissions: user?.permissions,
      isExternalAccount: user?.is_external_account === true,
    });
  }, [
    activeRole,
    user?.isLoggedIn,
    user?.is_external_account,
    user?.permissions,
    user?.role,
  ]);

  const [latestAnnouncements, setLatestAnnouncements] = useState([]);
  const [popupCandidateAnnouncements, setPopupCandidateAnnouncements] = useState([]);
  const [latestMeta, setLatestMeta] = useState({ total: 0, limit: 5 });
  const [feedLoading, setFeedLoading] = useState(false);
  const [feedError, setFeedError] = useState('');
  const { data: pendingApprovalCount, refresh: refreshPendingCount } = useBadgeQuery({
    queryKey: ['announcement-pending-count'],
    enabled: activeRole === 'admin' && canViewAdminAnnouncements,
    initialValue: 0,
    queryFn: async () => {
      const payload = await getAdminAnnouncementPendingCount();
      const count = payload?.pendingCount;
      if (typeof count !== 'number' || !Number.isSafeInteger(count) || count < 0) {
        throw new Error('Invalid announcement pending count');
      }
      return count;
    },
  });
  const [popupAnnouncement, setPopupAnnouncement] = useState(null);
  const [viewerScope, setViewerScope] = useState(null);
  const [popupDismissalTokens, setPopupDismissalTokens] = useState([]);
  const [popupDismissalsReady, setPopupDismissalsReady] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);

  const markAnnouncementsAcknowledged = useCallback((announcementIds) => {
    const acknowledgedIds = new Set(
      (Array.isArray(announcementIds) ? announcementIds : [])
        .map((value) => Number(value))
        .filter((value) => Number.isInteger(value) && value > 0)
    );
    if (!acknowledgedIds.size) return;
    const acknowledgedAt = new Date().toISOString();
    const markAcknowledged = (items) =>
      items.map((item) =>
        acknowledgedIds.has(Number(item.id))
          ? { ...item, viewerAcknowledged: true, acknowledgedAt }
          : item
      );
    setPopupCandidateAnnouncements(markAcknowledged);
    setLatestAnnouncements(markAcknowledged);
    setPopupAnnouncement((prev) =>
      acknowledgedIds.has(Number(prev?.id)) ? null : prev
    );
  }, []);

  const fetchFeed = useCallback(
    async ({ limit = 5, offset = 0 } = {}) => {
      if (!activeRole) return { announcements: [], total: 0 };
      try {
        return await listAnnouncementFeed({
          limit: Math.max(1, Math.min(limit, 100)),
          offset: Math.max(0, offset || 0),
        });
      } catch (error) {
        throw new Error(error?.message || 'お知らせの取得に失敗しました');
      }
    },
    [activeRole]
  );

  const refreshFeed = useCallback(async () => {
    if (!activeRole) {
      setLatestAnnouncements([]);
      setPopupCandidateAnnouncements([]);
      setPopupAnnouncement(null);
      setViewerScope(null);
      setPopupDismissalTokens([]);
      setPopupDismissalsReady(false);
      setFeedError('');
      setFeedLoading(false);
      return { announcements: [] };
    }
    setFeedLoading(true);
    setFeedError('');
    try {
      const data = await fetchFeed({ limit: POPUP_FEED_LIMIT });
      const announcements = data?.announcements || [];
      const nextViewerScope = data?.viewerScope || null;
      const localTokens = readDismissalTokens(nextViewerScope);
      const localTokenSet = new Set(localTokens);
      const pendingServerSyncIds = announcements
        .filter((item) => item?.viewerAcknowledged !== true && localTokenSet.has(buildPopupToken(item)))
        .map((item) => Number(item.id))
        .filter((id) => Number.isInteger(id) && id > 0);
      if (pendingServerSyncIds.length) {
        dismissAnnouncements(pendingServerSyncIds)
          .then((payload) => markAnnouncementsAcknowledged(payload?.acknowledgedIds))
          .catch(() => {});
      }
      setViewerScope(nextViewerScope);
      setPopupDismissalTokens(localTokens);
      setPopupDismissalsReady(true);
      setLatestAnnouncements(announcements.slice(0, LATEST_FEED_LIMIT));
      setPopupCandidateAnnouncements(announcements);
      setLatestMeta({ total: data?.total || 0, limit: LATEST_FEED_LIMIT });
      setLastSyncedAt(Date.now());
      return data;
    } catch (error) {
      setLatestAnnouncements([]);
      setPopupCandidateAnnouncements([]);
      setLatestMeta({ total: 0, limit: 5 });
      setFeedError(error?.message || 'お知らせの取得に失敗しました');
      throw error;
    } finally {
      setFeedLoading(false);
    }
  }, [activeRole, fetchFeed, markAnnouncementsAcknowledged]);

  useEffect(() => {
    if (!activeRole) {
      setViewerScope(null);
      setPopupDismissalTokens([]);
      setPopupDismissalsReady(false);
      setPopupAnnouncement(null);
      return;
    }
    setPopupDismissalsReady(false);
    refreshFeed().catch(() => {});
    refreshPendingCount().catch(() => {});
  }, [activeRole, refreshFeed, refreshPendingCount]);

  const evaluatePopupCandidate = useCallback(
    (announcements) => {
      if (!activeRole || !popupDismissalsReady) {
        setPopupAnnouncement(null);
        return;
      }
      const latestCandidate = selectLatestPopupAnnouncement(announcements);
      const token = buildPopupToken(latestCandidate);
      if (
        !latestCandidate ||
        latestCandidate.viewerAcknowledged === true ||
        !token ||
        popupDismissalTokens.includes(token)
      ) {
        setPopupAnnouncement(null);
        return;
      }
      setPopupAnnouncement(latestCandidate);
    },
    [activeRole, popupDismissalTokens, popupDismissalsReady]
  );

  useEffect(() => {
    evaluatePopupCandidate(popupCandidateAnnouncements);
  }, [popupCandidateAnnouncements, evaluatePopupCandidate]);

  const dismissPopupAnnouncement = useCallback(async (targetAnnouncement) => {
    if (!targetAnnouncement || !activeRole) return;
    const targetId = Number(targetAnnouncement.id);
    if (!Number.isInteger(targetId) || targetId <= 0) return;
    const nextTokens = Array.from(new Set([
      ...popupDismissalTokens,
      buildPopupToken(targetAnnouncement),
    ].filter(Boolean))).slice(-MAX_LOCAL_DISMISSALS);
    writeDismissalTokens(viewerScope, nextTokens);
    setPopupDismissalTokens(nextTokens);
    markAnnouncementsAcknowledged([targetId]);
    try {
      const payload = await dismissAnnouncements([targetId]);
      markAnnouncementsAcknowledged(payload?.acknowledgedIds);
    } catch (error) {
      console.warn('[AnnouncementContext] popup acknowledge failed', error);
    }
  }, [activeRole, markAnnouncementsAcknowledged, popupDismissalTokens, viewerScope]);

  const acknowledgePopup = useCallback(
    async (targetAnnouncement = popupAnnouncement) => {
      if (!targetAnnouncement) return;
      await dismissPopupAnnouncement(targetAnnouncement);
    },
    [dismissPopupAnnouncement, popupAnnouncement]
  );

  const dismissPopup = acknowledgePopup;

  const contextValue = useMemo(
    () => ({
      role: activeRole,
      latestAnnouncements,
      feedLoading,
      feedError,
      lastSyncedAt,
      refreshFeed,
      fetchFeed,
      pendingApprovalCount,
      refreshPendingCount,
      popupAnnouncement,
      acknowledgePopup,
      dismissPopup,
      latestMeta,
    }),
    [
      activeRole,
      latestAnnouncements,
      feedLoading,
      feedError,
      lastSyncedAt,
      refreshFeed,
      fetchFeed,
      pendingApprovalCount,
      refreshPendingCount,
      popupAnnouncement,
      acknowledgePopup,
      dismissPopup,
      latestMeta,
    ]
  );

  return (
    <AnnouncementContext.Provider value={contextValue}>{children}</AnnouncementContext.Provider>
  );
}

export const useAnnouncements = () => {
  const ctx = useContext(AnnouncementContext);
  if (!ctx) {
    throw new Error('useAnnouncements must be used within AnnouncementProvider');
  }
  return ctx;
};
